import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type RefObject,
} from 'react';

import {
  appendExampleText,
  appendExampleTextRichHtml,
  exampleAlreadyInserted,
  exampleAlreadyInsertedRichHtml,
} from '../utils/fieldExampleInsert';

import type {
  FieldValues,
  Path,
  PathValue,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import type { TextInput } from 'react-native';

export type InsertExampleSelection = { start: number; end: number };

export type UseInsertExampleAtEndArgs<T extends FieldValues> = {
  name: Path<T>;
  maxLength: number;
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
  updateFormData: (field: string, value: string) => void;
  inputRef: RefObject<TextInput | null>;
  onInserted?: () => void;
  /**
   * For sanitized-HTML fields (e.g. `oppProjectDescription`). Wraps the
   * example in `<p>` paragraphs so line breaks survive in the rich editor.
   */
  richHtml?: boolean;
};

export type UseInsertExampleAtEndResult = {
  insert: (example: string) => boolean;
  selection: InsertExampleSelection | undefined;
  onSelectionChange: () => void;
  /** Call from overlay `onDismiss`. Focuses only if `insert` succeeded this open. */
  focusAfterDismiss: () => void;
};

function scheduleDeferred(run: () => void): void {
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(run);
    return;
  }
  setTimeout(run, 0);
}

/**
 * Inserts static example copy at the end of an RHF text field.
 *
 * Owns the non-string guard, dual write (RHF + draft store) of the same
 * string in one synchronous turn, dirty/validate flags, deferred focus after
 * overlay dismiss (`focusAfterDismiss`), and one-shot caret placement.
 * Do not reimplement those in an RHF consumer. Deliverables comments (#1473)
 * must not call this hook — they are not RHF. Use `appendExampleText` +
 * `onAdd` / `onEdit` and `scheduleDeferred` instead.
 */
export function useInsertExampleAtEnd<T extends FieldValues>(
  args: UseInsertExampleAtEndArgs<T>,
): UseInsertExampleAtEndResult {
  const {
    name,
    maxLength,
    getValues,
    setValue,
    updateFormData,
    inputRef,
    onInserted,
    richHtml = false,
  } = args;

  const [selection, setSelection] = useState<
    InsertExampleSelection | undefined
  >(undefined);
  const unmountedRef = useRef(false);
  const pendingFocusRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
    };
  }, []);

  const onSelectionChange = useCallback(() => {
    setSelection(undefined);
  }, []);

  const focusAfterDismiss = useCallback((): void => {
    if (!pendingFocusRef.current) {
      return;
    }
    pendingFocusRef.current = false;
    scheduleDeferred(() => {
      if (unmountedRef.current) {
        return;
      }
      inputRef.current?.focus();
    });
  }, [inputRef]);

  const insert = useCallback(
    (example: string): boolean => {
      const raw = getValues(name);
      const current = typeof raw === 'string' ? raw : '';
      const already = richHtml
        ? exampleAlreadyInsertedRichHtml(current, example)
        : exampleAlreadyInserted(current, example);
      if (already) {
        return false;
      }
      const { value, inserted } = richHtml
        ? appendExampleTextRichHtml(current, example, maxLength)
        : appendExampleText(current, example, maxLength);
      if (!inserted) {
        return false;
      }
      setValue(name, value as PathValue<T, Path<T>>, {
        shouldDirty: true,
        shouldValidate: true,
      });
      updateFormData(String(name), value);
      onInserted?.();
      setSelection({ start: value.length, end: value.length });
      pendingFocusRef.current = true;
      return true;
    },
    [
      getValues,
      maxLength,
      name,
      onInserted,
      richHtml,
      setValue,
      updateFormData,
    ],
  );

  return { insert, selection, onSelectionChange, focusAfterDismiss };
}
